import React from "react"
import { Box, Chip, Typography, List, ListItem } from "@mui/joy"
import { Icon } from "../../assets"
import { LegalEntityAvatar, PersonAvatar, Tag } from "../Snippet"
import { Anchor } from "../Typography"
import { useTranslation } from 'react-i18next'
import { useFormatter } from "src/mtska-frontend-app-component/utils"

const InfoHeaderAudianceLead = ({ item }) => {
	const { t } = useTranslation()
	const { formatDate } = useFormatter()

	return (
		<Box
			className="info-header info-header-person"
			sx={{
				display: 'flex',
				flexWrap: 'wrap',
				justifyContent: 'space-between',
				alignItems: 'center',
				borderRadius: 'lg',
				bgcolor: '#FFFFFF',
				gap: 2
			}}
		>
			<Box sx={{ display: 'flex', flexShrink: 1, alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
				{item?.type === 'retail' && <PersonAvatar firstname={item?.firstname} lastname={item?.lastname} url={item?.picture?.url} item={item} />}
				{item?.type === 'business' && <LegalEntityAvatar name={item?.companyName} url={item?.picture?.url} item={item} />}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
						{item?.company && (
							<Chip color="neutral" variant="soft" startDecorator={<Icon icon="faBuilding" />}>
								{item.company}
							</Chip>
						)}
						<Chip color="primary" variant="soft">{t(item?.type) || '-'}</Chip>
						<Chip color="primary" variant="soft">{t(item?.dualFuel) || '-'}</Chip>
					</Box>
					<Typography level="h2" sx={{ mb: 1.5, mt: 1, color: "var(--customer-800)", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{item?.type === 'retail' && `${item?.firstname} ${item?.lastname}`}
						{item?.type === 'business' && item?.companyName}
					</Typography>
					<List orientation="horizontal" sx={{ gap: 2, m: 0, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
						{item?.email && (
							<ListItem sx={{ p: 0 }}>
								<Anchor icon="faEnvelope" iconfamily="fal" href={`mailto:${item?.email}`}>
									{item?.email}
								</Anchor>
							</ListItem>
						)}
						{item?.telephone && (
							<ListItem sx={{ p: 0 }}>
								<Anchor icon="faPhone" iconfamily="fal" href={`tel:${item?.telephone || ''}`}>
									{item?.telephone}
								</Anchor>
							</ListItem>
						)}
						{item?.mobile && (
							<ListItem sx={{ p: 0 }}>
								<Anchor icon="faPhone" iconfamily="fal" href={`tel:${item?.mobile || ''}`}>
									{item?.mobile}
								</Anchor>
							</ListItem>
						)}
					</List>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
						{item?.segments?.length ? (
							<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
								{item.segments.map((seg, i) => (
									<Chip key={i} color="neutral" variant="soft" sx={{ flex: 1, backgroundColor: seg.color + '80' ?? '#00000005' }}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: seg.color ?? '#00000020' }} />
											{seg.name}
										</Box>
									</Chip>
								))}
							</Box>
						) : (
							<Chip color="warning" variant="soft">{t('No segment')}</Chip>
						)}
						{item?.lastCampaign && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
								<Typography level="body-sm">Ultima campagna</Typography>
								<Chip color="neutral" variant="soft">
									{item?.lastCampaign.name} | {formatDate(item?.lastCampaign.start_at)} | {formatDate(item?.lastCampaign.end_at)}
								</Chip>
							</Box>
						)}
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
							<Typography level="body-sm">Fonte</Typography>
							<Chip color="neutral" variant="soft">{item?.source || '-'}</Chip>
						</Box>
						{item?.deliveries?.length ? (
							<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
								<Typography level="body-sm">Forniture</Typography>
								<Chip color="neutral" variant="soft">
									{(() => {
										const arr = item.deliveries.map(d => d.city)
										const first = arr.slice(0, 2).join(' | ')
										const extra = arr.length > 2 ? ` (+${arr.length - 2})` : ''
										return first + extra
									})()}
								</Chip>
							</Box>
						) : (
							<Chip color="warning" variant="soft">{t('No deliveries')}</Chip>
						)}

					</Box>
				</Box>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 2, minWidth: 120, flexShrink: 0 }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
					<Typography sx={{ textAlign: 'right', fontSize: '0.75rem' }}>Privacy</Typography>
					<Tag color="success" dot label={
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
							{`${formatDate(item?.privacyStart_at)} | ${formatDate(item?.privacyEnd_at)}`}
						</Box>
					} />
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
					<Typography sx={{ textAlign: 'right', fontSize: '0.75rem' }}>RPO</Typography>
					<Tag color="success" dot label={
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
							{item?.rpo ? 'Yes' : 'No'}
						</Box>
					} />
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
					<Typography sx={{ textAlign: 'right', fontSize: '0.75rem' }}>Blacklist</Typography>
					<Tag color="success" dot label={
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
							{item?.isBlacklist ? 'Yes' : 'No'}
						</Box>
					} />
				</Box>
			</Box>
		</Box>
	)
}

export default InfoHeaderAudianceLead